/**
 * Pure logic behind the allocation screen.
 *
 * Split out of the page for the same reason as `tripsBoard.ts`: the arithmetic here
 * decides what a cooperant is paid, and it must be testable without rendering.
 */
import type { TripItemAllocationView } from "../../api/transport";

/** What the operator has typed, keyed by item id. Strings, because a half-typed
 * number is a legitimate intermediate state and must not become NaN mid-keystroke. */
export type Draft = Record<string, string>;

/**
 * The sum of what has been typed, and whether every row has a usable figure.
 *
 * A blank or unparseable row does NOT contribute zero — it makes the whole draft
 * incomplete. Treating it as zero would let a draft look balanced while a supplier
 * is silently allocated nothing.
 */
export function draftTotal(items: TripItemAllocationView[], draft: Draft): {
  kg: number;
  complete: boolean;
} {
  let kg = 0;
  let complete = true;
  for (const i of items) {
    const raw = (draft[i.itemId] ?? "").trim();
    const n = raw === "" ? NaN : Number(raw);
    if (!Number.isFinite(n) || n <= 0) {
      complete = false;
      continue;
    }
    kg += n;
  }
  return { kg: round2(kg), complete };
}

/**
 * How far the typed total is from the trip's net.
 *
 * Returned in kilograms and signed, so the screen can say "3,40 kg too much"
 * rather than a bare red border. Null when there is no net to compare against —
 * an unweighed trip cannot be allocated at all.
 */
export function offBy(netKg: number | null, total: number): number | null {
  if (netKg == null) return null;
  return round2(total - netKg);
}

/**
 * Whether the draft may be signed.
 *
 * The sum must land on the net exactly, to the hundredth. This mirrors the server
 * rule rather than softening it: how much arrived may differ from what was
 * announced, but how that arrival was divided is arithmetic and may not differ at
 * all. The client checks only so the operator finds out before the round trip.
 */
export function canSign(
  netKnown: boolean,
  netKg: number | null,
  items: TripItemAllocationView[],
  draft: Draft,
): boolean {
  if (!netKnown || netKg == null || items.length === 0) return false;
  const { kg, complete } = draftTotal(items, draft);
  if (!complete) return false;
  return offBy(netKg, kg) === 0;
}

/**
 * The pro-rata proposal as a starting draft.
 *
 * Empty when the server offered no proposal — which happens when any item declared
 * nothing, because then there is no ratio to apply and an equal split would be a
 * guess wearing a formula.
 */
export function proposalAsDraft(items: TripItemAllocationView[]): Draft {
  const out: Draft = {};
  for (const i of items) {
    if (i.proposedKg == null) return {};
    out[i.itemId] = i.proposedKg.toFixed(2);
  }
  return out;
}

/** Whatever is already signed, as a draft — so reopening the screen shows the
 * standing split rather than an empty form. */
export function signedAsDraft(items: TripItemAllocationView[]): Draft {
  const out: Draft = {};
  for (const i of items) {
    if (i.allocatedKg != null) out[i.itemId] = i.allocatedKg.toFixed(2);
  }
  return out;
}

function round2(n: number): number {
  // Kilograms carry two decimals in the database; floating point addition of
  // 8000.01-style figures otherwise drifts into 0.000000001 territory and the
  // exact-match rule would reject a draft that is in fact exact.
  return Math.round((n + Number.EPSILON) * 100) / 100;
}
